import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useZona } from "@/context/ZonaContext";
import {
  Users, Search, Loader2, Building2, TrendingUp, AlertTriangle,
  FileText, Trophy, Target, X, Info, Merge, Undo2, History, Landmark, Activity, MapPin,
} from "lucide-react";
import { BulkMergeMunicipiosModal } from "@/components/crm/BulkMergeMunicipiosModal";

const fmt = (v) => (Number(v) || 0).toLocaleString("es-ES", { maximumFractionDigits: 2 });

const SALUD_CFG = {
  verde: { label: "Saludable", dot: "bg-emerald-500", ring: "border-emerald-200 bg-emerald-50/50", text: "text-emerald-700" },
  ambar: { label: "A vigilar", dot: "bg-amber-500", ring: "border-amber-200 bg-amber-50/50", text: "text-amber-700" },
  rojo: { label: "En riesgo", dot: "bg-rose-500", ring: "border-rose-200 bg-rose-50/50", text: "text-rose-700" },
  sin_datos: { label: "Sin resultados resueltos", dot: "bg-gray-300", ring: "border-gray-200 bg-gray-50", text: "text-gray-500" },
};

const Semaforo = ({ s }) => {
  const cfg = SALUD_CFG[s.nivel] || SALUD_CFG.sin_datos;
  return (
    <div className={`rounded-xl border p-3 mb-4 ${cfg.ring}`} data-testid="cliente-salud">
      <div className="flex items-center gap-2 mb-2">
        <Activity size={14} className={cfg.text} />
        <span className="text-[11px] font-bold text-gray-700">Salud de la cuenta</span>
        <span className={`inline-flex items-center gap-1.5 ml-auto text-[11px] font-bold ${cfg.text}`} data-testid="cliente-salud-nivel">
          <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} /> {cfg.label}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg bg-white/70 border border-gray-100 p-2 text-center" title="Ganadas sobre resueltas (ganadas+perdidas)">
          <p className="text-[9px] uppercase text-gray-400">Hit-rate</p>
          <p className="text-base font-black text-gray-900">{s.hit_rate_pct != null ? `${s.hit_rate_pct}%` : "—"}</p>
          <p className="text-[9px] text-gray-400">{s.n_ganadas}G · {s.n_perdidas}P{s.n_pendientes_resolucion ? ` · ${s.n_pendientes_resolucion} pend.` : ""}</p>
        </div>
        <div className="rounded-lg bg-white/70 border border-gray-100 p-2 text-center" title="Importe ganado sobre importe presentado">
          <p className="text-[9px] uppercase text-gray-400">Ganado / presentado</p>
          <p className="text-base font-black text-gray-900">{s.eficiencia_economica_pct != null ? `${s.eficiencia_economica_pct}%` : "—"}</p>
          <p className="text-[9px] text-gray-400">{fmt(s.importe_ganado_mm)} / {fmt(s.importe_presentado_mm)} M€</p>
        </div>
        <div className="rounded-lg bg-white/70 border border-gray-100 p-2 text-center" title="Ofertas sin incumbente acreditado (tarea de investigación)">
          <p className="text-[9px] uppercase text-gray-400">Por investigar</p>
          <p className={`text-base font-black ${s.incumbentes_por_investigar > 0 ? "text-amber-700" : "text-gray-900"}`}>{s.incumbentes_por_investigar}</p>
          <p className="text-[9px] text-gray-400">incumbentes</p>
        </div>
      </div>
    </div>
  );
};

const Ficha = ({ nombre, onClose }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios.get(`${API}/clientes360/ficha`, { params: { nombre } })
      .then((r) => { if (alive) setD(r.data); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [nombre]);

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="cliente-ficha-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <Building2 size={16} className="text-indigo-600" /> {nombre}
            {d?.fusionado && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" title={`Cuenta fusionada: ${d.incluye?.join(", ")}`}>fusionada · {d.incluye?.length}</span>}
          </h2>
          <button onClick={onClose} data-testid="cliente-ficha-close" className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {loading && <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center"><Loader2 className="animate-spin" size={16} /> Cargando ficha…</div>}
          {d && d.existe && (
            <>
              {d.salud && <Semaforo s={d.salud} />}
              {d.incluye_aprendidos?.length > 0 && (
                <p className="text-[10px] text-indigo-600 mb-3 flex items-start gap-1" data-testid="cliente-aprendidos-nota">
                  <Landmark size={11} className="mt-0.5 shrink-0" /> {d.incluye_aprendidos.length} nombre(s) atribuido(s) automáticamente por patrón aprendido: {d.incluye_aprendidos.join(", ")}.
                </p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                {[
                  ["Ofertas", d.economia.n_ofertas, Target, "text-blue-700"],
                  ["Presentadas", d.economia.n_presentadas, FileText, "text-emerald-700"],
                  ["Adjudicadas", d.economia.n_adjudicadas, Trophy, "text-amber-700"],
                  ["Oportunidades", d.economia.n_oportunidades, TrendingUp, "text-violet-700"],
                ].map(([l, v, Icon, col]) => (
                  <div key={l} className="rounded-xl border border-gray-200 p-3">
                    <p className="text-[10px] uppercase tracking-wide text-gray-400 flex items-center gap-1"><Icon size={11} className={col} /> {l}</p>
                    <p className="text-xl font-black text-gray-900">{v}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-2 text-center"><p className="text-[9px] uppercase text-gray-400">Importe total</p><p className="text-sm font-bold">{fmt(d.economia.importe_total_mm)} M€</p></div>
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-2 text-center"><p className="text-[9px] uppercase text-gray-400">Presentado</p><p className="text-sm font-bold text-emerald-700">{fmt(d.economia.importe_presentado_mm)} M€</p></div>
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-2 text-center"><p className="text-[9px] uppercase text-gray-400">Adjudicado</p><p className="text-sm font-bold text-amber-700">{fmt(d.economia.importe_adjudicado_mm)} M€</p></div>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4 text-[11px]">
                {d.identidad.tipo_cliente && <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">{d.identidad.tipo_cliente}</span>}
                {d.identidad.dgs.map((x) => <span key={x} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{x}</span>)}
                {d.identidad.provincias.slice(0, 6).map((x) => <span key={x} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{x}</span>)}
              </div>

              {/* Competidores / incumbentes */}
              <div className="rounded-xl border border-gray-200 p-3 mb-4" data-testid="cliente-competidores">
                <p className="text-[11px] font-bold text-gray-700 mb-2">Incumbentes / competidores conocidos ({d.incumbente.conocidos})</p>
                {d.competidores.length === 0 && <p className="text-[11px] text-gray-400">Ningún incumbente acreditado para este cliente.</p>}
                <div className="flex flex-wrap gap-1.5">
                  {d.competidores.map((c) => (
                    <span key={c.incumbent} className="text-[11px] px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" title={c.fuente || "sin fuente"}>
                      {c.incumbent} · {c.n}
                    </span>
                  ))}
                </div>
                {d.incumbente.ofertas_sin_incumbente > 0 && (
                  <p className="text-[10px] text-amber-700 mt-2 flex items-start gap-1"><AlertTriangle size={11} className="mt-0.5 shrink-0" /> {d.incumbente.ofertas_sin_incumbente} oferta(s) sin incumbente acreditado. {d.incumbente.nota}</p>
                )}
              </div>

              {/* Ofertas */}
              <div className="overflow-x-auto border border-gray-200 rounded-xl mb-3">
                <table className="w-full text-[11px]">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-[9px]">
                    <tr><th className="px-2 py-2 text-left">Expediente</th><th className="px-2 py-2 text-left">Objeto</th><th className="px-2 py-2 text-left">Estado</th><th className="px-2 py-2 text-right">Importe (M€)</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-testid="cliente-ofertas-table">
                    {d.ofertas.map((o) => (
                      <tr key={o.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 text-gray-500 max-w-[120px] truncate" title={o.expediente}>{o.expediente || "—"}</td>
                        <td className="px-2 py-1.5 text-gray-800 max-w-[260px] truncate" title={o.objeto}>{o.objeto || "—"}</td>
                        <td className="px-2 py-1.5"><span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{o.estado || "—"}</span></td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmt(o.importe_mm)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {d.exclusiones?.length > 0 && (
                <p className="text-[10px] text-gray-400 flex items-start gap-1"><Info size={11} className="mt-0.5 shrink-0" /> {d.exclusiones.join(" · ")}</p>
              )}
            </>
          )}
          {d && !d.existe && <p className="text-sm text-gray-500 py-6 text-center">{d.mensaje}</p>}
        </div>
      </div>
    </div>
  );
};

const AmbGroup = ({ g, busy, onResolve }) => {
  const [canon, setCanon] = useState(g.candidatos[0]);
  const isBusy = busy === g.clave;
  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5 border-b border-amber-100 last:border-0" data-testid={`amb-group-${g.clave}`}>
      <span className="text-amber-900 font-medium">{g.candidatos.join("  ↔  ")}</span>
      <div className="ml-auto flex items-center gap-1">
        <span className="text-[10px] text-amber-700">Cuenta canónica:</span>
        <select value={canon} onChange={(e) => setCanon(e.target.value)} disabled={isBusy} className="text-[10px] border border-amber-300 rounded px-1 py-0.5 bg-white max-w-[180px]" data-testid={`amb-canon-${g.clave}`}>
          {g.candidatos.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button disabled={isBusy} onClick={() => onResolve(g, "merge", canon)} data-testid={`amb-merge-${g.clave}`} className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1">
          {isBusy ? <Loader2 size={10} className="animate-spin" /> : <Merge size={10} />} Es la misma cuenta
        </button>
        <button disabled={isBusy} onClick={() => onResolve(g, "distinct")} data-testid={`amb-distinct-${g.clave}`} className="text-[10px] font-semibold px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50">
          Son distintas
        </button>
      </div>
    </div>
  );
};

export default function ClientesView() {
  const { zonaGlobal } = useZona();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [showAmb, setShowAmb] = useState(false);
  const [busy, setBusy] = useState(null);
  const [merges, setMerges] = useState(null);
  const [showTraza, setShowTraza] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/clientes360`, { params: zonaGlobal ? { zona: zonaGlobal } : {} }).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  const loadMerges = () => axios.get(`${API}/clientes360/merges`).then((r) => setMerges(r.data));
  useEffect(() => { load(); }, [zonaGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (group, decision, canonical) => {
    setBusy(group.clave);
    try {
      await axios.post(`${API}/clientes360/merges`, {
        decision, clave: group.clave, members: group.candidatos, canonical: canonical || null,
      });
      load();
      if (showTraza) loadMerges();
    } finally { setBusy(null); }
  };
  const undo = async (id) => {
    await axios.delete(`${API}/clientes360/merges/${id}`);
    loadMerges();
    load();
  };
  const toggleTraza = () => {
    const n = !showTraza;
    setShowTraza(n);
    if (n && !merges) loadMerges();
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const nq = norm(q);
    return data.clientes.filter((c) => !nq || norm(c.nombre).includes(nq));
  }, [data, q]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="clientes-360-page">
      <div className="flex items-center gap-2 mb-1">
        <Users className="text-indigo-600" size={22} />
        <h1 className="text-xl font-black text-gray-900">Clientes 360</h1>
      </div>
      <p className="text-xs text-gray-500 mb-4">Ficha comercial conectada: oportunidades, ofertas, incumbentes y economía por cuenta. Solo relaciones exactas; los nombres ambiguos van a cola, no se fusionan solos.</p>

      {zonaGlobal && data?.cobertura?.nota_zona && (
        <div className="flex items-start gap-2 bg-[#F0FDF4] border border-[#007934]/30 rounded-lg px-3 py-2 text-xs text-[#065f30] mb-4" data-testid="clientes-zona-banner">
          <MapPin size={14} className="mt-0.5 flex-shrink-0 text-[#007934]" />
          <span>{data.cobertura.nota_zona}</span>
        </div>
      )}

      {loading && <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center"><Loader2 className="animate-spin" size={16} /> Cargando cuentas…</div>}

      {data && (
        <>
          {/* Cobertura */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-3" data-testid="clientes-cobertura">
            {[
              ["Cuentas", data.cobertura.total_clientes],
              ["Con ofertas", data.cobertura.con_ofertas],
              ["Con oportunidades", data.cobertura.con_oportunidades],
              ["Fusionadas", data.cobertura.cuentas_fusionadas],
              ["Aprendidos", data.cobertura.nombres_aprendidos],
              ["En cola ambigüedad", data.cobertura.en_cola_ambiguedad],
              ["No identificables", data.cobertura.registros_no_identificables],
            ].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-gray-200 p-2.5 text-center">
                <p className="text-[9px] uppercase tracking-wide text-gray-400">{l}</p>
                <p className="text-lg font-black text-gray-900">{v}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div className="flex items-center border border-gray-200 rounded-lg px-2 py-1 gap-1">
              <Search size={12} className="text-gray-400" />
              <input data-testid="clientes-search" placeholder="Buscar cuenta (tolera tildes)…" value={q} onChange={(e) => setQ(e.target.value)} className="text-xs outline-none w-64" />
            </div>
            <span className="text-[11px] text-gray-400">{filtered.length} cuentas</span>
            {data.ambiguos.length > 0 && (
              <button onClick={() => setShowAmb((s) => !s)} data-testid="clientes-ambiguedad-toggle" className="text-[11px] font-semibold px-2 py-1 rounded-md border border-amber-300 bg-amber-50 text-amber-700 inline-flex items-center gap-1">
                <AlertTriangle size={11} /> Cola de ambigüedad ({data.ambiguos.length})
              </button>
            )}
            <button onClick={() => setShowBulk(true)} data-testid="clientes-bulk-municipios-btn" className="text-[11px] font-semibold px-2 py-1 rounded-md border border-indigo-300 bg-indigo-50 text-indigo-700 inline-flex items-center gap-1 hover:bg-indigo-100">
              <Landmark size={11} /> Fusión masiva de municipios
            </button>
            <button onClick={toggleTraza} data-testid="clientes-traza-toggle" className="text-[11px] font-semibold px-2 py-1 rounded-md border border-gray-300 bg-white text-gray-600 inline-flex items-center gap-1 hover:bg-gray-50">
              <History size={11} /> Traza de fusiones
            </button>
          </div>

          {showTraza && (
            <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 mb-3 text-[11px]" data-testid="clientes-traza-panel">
              <p className="font-bold text-gray-700 mb-1.5">Traza de decisiones de cuenta</p>
              {!merges && <p className="text-gray-400">Cargando…</p>}
              {merges && (
                <>
                  <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Activas ({merges.activos.length})</p>
                  {merges.activos.length === 0 && <p className="text-gray-400 mb-2">Sin fusiones ni decisiones activas.</p>}
                  {merges.activos.map((m) => (
                    <div key={m.id} className="flex items-center gap-2 py-0.5" data-testid={`traza-activo-${m.id}`}>
                      <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${m.decision === "merge" ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}>
                        {m.decision === "merge" ? "Fusión" : "Distintas"}
                      </span>
                      <span className="text-gray-700">
                        {m.decision === "merge" ? <><b>{m.canonical}</b> ← {m.members.filter((x) => x !== m.canonical).join(", ")}</> : m.members.join("  ↔  ")}
                      </span>
                      <span className="text-gray-400 text-[10px]">· {m.created_by} · {(m.created_at || "").slice(0, 10)}</span>
                      <button onClick={() => undo(m.id)} data-testid={`traza-undo-${m.id}`} className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-white inline-flex items-center gap-1">
                        <Undo2 size={10} /> Deshacer
                      </button>
                    </div>
                  ))}
                  {merges.historial.length > 0 && (
                    <>
                      <p className="text-[10px] uppercase tracking-wide text-gray-400 mt-2 mb-1">Histórico ({merges.historial.length})</p>
                      {merges.historial.slice(0, 15).map((m) => (
                        <div key={m.id} className="py-0.5 text-gray-400">
                          {m.decision === "merge" ? "Fusión" : "Distintas"}: {m.members.join("  ↔  ")} · {m.created_by} · {(m.created_at || "").slice(0, 10)}
                          {m.undone_by && <span className="text-rose-400"> · deshecha por {m.undone_by}</span>}
                          {m.superseded_by && <span className="text-amber-500"> · sustituida</span>}
                        </div>
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {showAmb && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 mb-3 text-[11px]" data-testid="clientes-ambiguedad-panel">
              <p className="font-bold text-amber-800 mb-1.5">Candidatos a reconciliar — marca si son la misma cuenta (se fusionan con traza) o distintas</p>
              {data.ambiguos.slice(0, 60).map((a) => (
                <AmbGroup key={a.clave} g={a} busy={busy} onResolve={resolve} />
              ))}
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-200 overflow-auto" style={{ maxHeight: "60vh" }}>
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-500 uppercase text-[9px] sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-center w-10" title="Salud de cuenta (hit-rate)">Salud</th>
                  <th className="px-3 py-2 text-left">Cuenta</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-right">Ofertas</th>
                  <th className="px-3 py-2 text-right">Oportunidades</th>
                  <th className="px-3 py-2 text-right">Presentadas</th>
                  <th className="px-3 py-2 text-right">Importe (M€)</th>
                  <th className="px-3 py-2 text-center">Incumbente</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-testid="clientes-table">
                {filtered.slice(0, 300).map((c) => (
                  <tr key={c.nombre} className="hover:bg-indigo-50 cursor-pointer" onClick={() => setSel(c.nombre)} data-testid={`cliente-row-${c.nombre}`}>
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${SALUD_CFG[c.salud_nivel]?.dot || "bg-gray-300"}`} title={c.hit_rate_pct != null ? `${SALUD_CFG[c.salud_nivel]?.label} · hit-rate ${c.hit_rate_pct}%` : "Sin resultados resueltos"} data-testid={`cliente-salud-dot-${c.nombre}`} />
                    </td>
                    <td className="px-3 py-2 font-semibold text-gray-800 max-w-[260px] truncate" title={c.incluye?.length > 1 ? `Incluye: ${c.incluye.join(", ")}` : c.nombre}>
                      {c.nombre}
                      {c.fusionado && <span className="ml-1.5 text-[9px] font-bold px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-0.5 align-middle" data-testid={`cliente-fusionada-${c.nombre}`}><Merge size={9} /> fusionada</span>}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{c.tipo_cliente || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.n_ofertas}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.n_oportunidades}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.n_presentadas}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(c.importe_total_mm)}</td>
                    <td className="px-3 py-2 text-center">{c.incumbente_conocido ? <span className="text-emerald-600">✓</span> : <span className="text-gray-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length > 300 && <p className="text-[10px] text-gray-400 mt-1">Mostrando las 300 primeras · afina con el buscador.</p>}
          <p className="text-[10px] text-gray-400 mt-2 flex items-start gap-1"><Info size={11} className="mt-0.5 shrink-0" /> {data.cobertura.exclusiones.join(" · ")}</p>
        </>
      )}

      {sel && <Ficha nombre={sel} onClose={() => setSel(null)} />}
      {showBulk && (
        <BulkMergeMunicipiosModal
          onClose={() => setShowBulk(false)}
          onDone={() => { setShowBulk(false); load(); if (showTraza) loadMerges(); }}
        />
      )}
    </div>
  );
}
